/**
 * Text-date conversion for bulk import — convert when certain, refuse to guess.
 *
 * Real rosters carry dates typed as text ("25/03/1990"), which the row schema
 * rightly rejects: it demands ISO. Auto-converting is safe ONLY when the
 * day/month order is provable; "03/04/1990" is March 4th in a US-locale sheet
 * and April 3rd in a Jordanian one, and silently picking wrong rewrites an
 * employee's birthday. So:
 *
 *  - The FILE votes on its format first. A spreadsheet is written by one hand
 *    in one locale: a single "25/03/1990" anywhere proves day-first for every
 *    date cell in the file; a single "03/25/1990" proves month-first. Both
 *    kinds of evidence in one file = a broken file — infer nothing.
 *  - With a proven format, every convertible cell converts.
 *  - Without one, only per-cell-certain values convert (a side > 12, or
 *    day == month where the orders agree). The rest are AMBIGUOUS: reported,
 *    never guessed.
 *
 * Year-first text ("1990/03/25") is order-unambiguous and always converts.
 * Two-digit years are deliberately NOT handled — "05" could be 1905 or 2005,
 * and pivot-window guessing on birthdates is the same trap with worse odds.
 * Cells already in ISO, or not date-shaped at all, are left untouched (the
 * schema remains the judge of those).
 */

const SEP = "[/.-]";
/** d/m/yyyy or m/d/yyyy — order unknown until inference. */
const DMY_OR_MDY_RE = new RegExp(`^(\\d{1,2})${SEP}(\\d{1,2})${SEP}(\\d{4})$`);
/** yyyy/m/d — order is unambiguous. */
const YMD_RE = new RegExp(`^(\\d{4})${SEP}(\\d{1,2})${SEP}(\\d{1,2})$`);

export type TextDateFormat = "day-first" | "month-first";

export interface TextDateConversion {
  /** The file-level format, when the file's own cells prove it. */
  format: TextDateFormat | null;
  /** raw trimmed cell → ISO yyyy-MM-dd, for every convertible cell. */
  converted: ReadonlyMap<string, string>;
  /** Cells that are date-shaped but unresolvable without guessing. */
  ambiguous: ReadonlySet<string>;
}

/**
 * A real calendar date can still be the wrong calendar: "1445-03-15" is a
 * Hijri date read as Gregorian, and it would start leave accrual in year 1445.
 * Years 1300–1500 read as Hijri; any other year outside 1900–2100 is simply
 * implausible for an employee record. Non-ISO values are the schema's call.
 */
export function dateYearProblem(isoDate: string): "hijri" | "implausible" | null {
  if (!/^\d{4}-/.test(isoDate)) return null;
  const year = Number(isoDate.slice(0, 4));
  if (year >= 1300 && year <= 1500) return "hijri";
  return year < 1900 || year > 2100 ? "implausible" : null;
}

function isRealDate(y: number, m: number, d: number): boolean {
  if (m < 1 || m > 12 || d < 1 || d > 31) return false;
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

function iso(y: number, m: number, d: number): string {
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${String(y)}-${pad(m)}-${pad(d)}`;
}

/**
 * Infer the file's format and convert every provable cell.
 *
 * `cells` is every raw value from the file's DATE columns (all of them
 * together — the writer's locale is a property of the file, not the column).
 */
export function convertTextDates(cells: readonly string[]): TextDateConversion {
  const dayFirstEvidence: string[] = [];
  const monthFirstEvidence: string[] = [];
  const twoPart: Array<{ raw: string; a: number; b: number; y: number }> = [];
  const converted = new Map<string, string>();
  const ambiguous = new Set<string>();

  for (const rawCell of cells) {
    const raw = rawCell.trim();
    if (raw === "" || converted.has(raw) || ambiguous.has(raw)) continue;
    // Already strict ISO — the schema accepts it as-is; nothing to convert.
    // ("1990-3-5" is NOT strict ISO and still gets zero-padded below.)
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) continue;

    const ymd = YMD_RE.exec(raw);
    if (ymd !== null) {
      const [, ys, ms, ds] = ymd;
      const y = Number(ys),
        m = Number(ms),
        d = Number(ds);
      if (isRealDate(y, m, d)) converted.set(raw, iso(y, m, d));
      continue; // not real → leave for the schema to reject with its own message
    }

    const two = DMY_OR_MDY_RE.exec(raw);
    if (two === null) continue; // ISO or not a date shape — not ours to touch
    const [, as, bs, ys] = two;
    const a = Number(as),
      b = Number(bs),
      y = Number(ys);
    const dmyValid = isRealDate(y, b, a); // a=day,  b=month
    const mdyValid = isRealDate(y, a, b); // a=month, b=day
    if (!dmyValid && !mdyValid) continue; // impossible either way — schema's problem
    if (dmyValid && !mdyValid) dayFirstEvidence.push(raw);
    if (mdyValid && !dmyValid) monthFirstEvidence.push(raw);
    twoPart.push({ raw, a, b, y });
  }

  const format: TextDateFormat | null =
    dayFirstEvidence.length > 0 && monthFirstEvidence.length === 0
      ? "day-first"
      : monthFirstEvidence.length > 0 && dayFirstEvidence.length === 0
        ? "month-first"
        : null; // no evidence, or contradictory evidence — infer nothing

  for (const { raw, a, b, y } of twoPart) {
    if (format === "day-first" && isRealDate(y, b, a)) {
      converted.set(raw, iso(y, b, a));
    } else if (format === "month-first" && isRealDate(y, a, b)) {
      converted.set(raw, iso(y, a, b));
    } else if (format === null) {
      // Per-cell certainty only: both orders agree (day == month), or exactly
      // one order forms a real date (a side > 12).
      const dmyValid = isRealDate(y, b, a);
      const mdyValid = isRealDate(y, a, b);
      if (a === b && dmyValid) converted.set(raw, iso(y, b, a));
      else if (dmyValid && !mdyValid) converted.set(raw, iso(y, b, a));
      else if (mdyValid && !dmyValid) converted.set(raw, iso(y, a, b));
      else ambiguous.add(raw);
    } else {
      // A proven format under which this cell is impossible ("31/02/…" in a
      // day-first file) — leave it; the schema names it a non-real date.
    }
  }

  return { format, converted, ambiguous };
}
